// src/modules/app-views/_apps/appmulti/receivables/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Tabs } from 'antd';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import RecordTab from './tabs/RecordTab';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const isMockStep = false;
const STYLES = {
  cardHeaderWrapper: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ReceivablesDocument = props => {
  // ---------------------------------------------------------------------------
  // PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  const { upsert, urlInfo, get } = useZustand('global', 'useApi');

  // ---------------------------------------------------------------------------
  // HOOKS & REFS
  // ---------------------------------------------------------------------------
  const { formRef, isInitializedRef } = useFormInstance({ data });

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const _id = urlInfo?.params?._id;

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  useEffect(() => {
    const childProps = { ...props, data, initTab, viewMode };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async ({ values, allValues }) => {
    try {
      const receivableId = values?._id || allValues?._id || data?._id;
      const isNewDocument = !_id || _id === 'add';

      const dataToSave = { ...values };
      if (isNewDocument && !dataToSave.status) dataToSave.status = 'new';

      await upsert({ dataSet: dataToSave, _id: receivableId });

      if (!isNewDocument) {
        await get();
      }
    } catch (err) {
      throw err;
    }
  };

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="value"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={['entityId', 'value', 'dueDate', 'billingType']}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeaderWrapper}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.__customer?.name || data?.description}
              watchField="countryCode"
            />
            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ReceivablesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data } = props;
  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <RecordTab />,
    },
  ];
}
